import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { useAdminT } from "./adminI18n";

export const RequestsTab = () => {
  const { t } = useAdminT();
  const [requests, setRequests] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    axios.get(`${API}/admin/access-requests`, { headers: authHeaders() }).then((res) => setRequests(res.data)).catch(() => setRequests([]));
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      await axios.post(`${API}/admin/access-requests/${id}/${action}`, {}, { headers: authHeaders() });
      toast.success(action === "approve" ? t("تمت الموافقة وأُرسل رابط الدخول إلى بريد العميلة") : t("تم رفض الطلب"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const pending = (requests || []).filter((r) => r.status === "pending");
  const history = (requests || []).filter((r) => r.status !== "pending");

  const statusBadge = (s) =>
    s === "approved" ? (
      <span className="text-[11px] tracking-wider bg-luxury-gold text-luxury-black font-bold px-3 py-1 rounded-full">{t("تمت الموافقة")}</span>
    ) : (
      <span className="text-[11px] tracking-wider border border-red-400 text-red-400 px-3 py-1 rounded-full">{t("مرفوض")}</span>
    );

  return (
    <div data-testid="admin-requests-tab">
      <h2 className="font-serif text-3xl text-luxury-bone mb-3">{t("طلبات الشراء")}</h2>
      <p className="text-luxury-gray text-sm mb-8">{t("طلبات الوصول القادمة من صفحة نجاح الدفع — اضغطي موافقة ليصل العميلة إيميل شكر مع رابط دخول مباشر للكورس")}</p>

      {requests === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>
      ) : (
        <>
          <h3 className="text-luxury-gold text-sm tracking-wider mb-4">
            {t("طلبات معلّقة")} ({pending.length})
          </h3>
          <div className="space-y-4 mb-12">
            {pending.map((r, i) => (
              <div key={r.id} data-testid={`request-row-${i}`} className="bg-luxury-surface border border-luxury-gold/40 p-5 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-luxury-bone font-semibold">
                    {r.name || "—"} <span className="text-luxury-gray font-normal" dir="ltr">{r.email}</span>
                  </p>
                  <p className="text-luxury-gray text-sm mt-1">
                    {r.course_title} — {(r.created_at || "").slice(0, 16).replace("T", " ")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button data-testid={`request-approve-${i}`} onClick={() => act(r.id, "approve")} disabled={!!busy}
                    className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
                    {busy === r.id + "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />}
                    {t("موافقة وإرسال الرابط")}
                  </button>
                  <button data-testid={`request-reject-${i}`} onClick={() => act(r.id, "reject")} disabled={!!busy}
                    className="flex items-center gap-2 border border-luxury-border text-red-400 px-4 py-2.5 rounded-full text-sm hover:border-red-400 transition-colors disabled:opacity-60">
                    <XCircle className="w-4 h-4" strokeWidth={1.5} /> {t("رفض")}
                  </button>
                </div>
              </div>
            ))}
            {pending.length === 0 && (
              <p className="text-luxury-gray text-center py-8 border border-luxury-border">{t("لا توجد طلبات معلّقة")}</p>
            )}
          </div>

          {history.length > 0 && (
            <>
              <h3 className="text-luxury-gold text-sm tracking-wider mb-4">{t("السجل")}</h3>
              <div className="space-y-3">
                {history.map((r, i) => (
                  <div key={r.id} data-testid={`request-history-${i}`} className="bg-luxury-surface border border-luxury-border p-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-luxury-bone text-sm">
                      {r.name || "—"} <span className="text-luxury-gray" dir="ltr">{r.email}</span> — {r.course_title}
                    </p>
                    {statusBadge(r.status)}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
